import type { LlmTaskId, LlmTaskMode } from "./types.js";

export interface LlmTaskSpec {
  id: LlmTaskId;
  mode: LlmTaskMode;
  version: string;
  maxOutputTokens: number;
  defaultProfile: "default";
}

function spec(id: LlmTaskId, mode: LlmTaskMode, maxOutputTokens: number): LlmTaskSpec {
  return { id, mode, version: "1", maxOutputTokens, defaultProfile: "default" };
}

export const LLM_TASK_REGISTRY = {
  "direct.episode-titles": spec("direct.episode-titles", "tool", 4096),
  "direct.batch-extract": spec("direct.batch-extract", "text", 16000),
  "direct.episode-synopsis": spec("direct.episode-synopsis", "tool", 2048),
  "direct.synopsis-reduction": spec("direct.synopsis-reduction", "tool", 4096),
  "direct.script-synopsis": spec("direct.script-synopsis", "tool", 2048),
  "direct.asset-grouping": spec("direct.asset-grouping", "text", 8000),
  "direct.asset-curation": spec("direct.asset-curation", "text", 20000),
  "direct.asset-curation-audit": spec("direct.asset-curation-audit", "text", 20000),
  "direct.location-canonicalization": spec("direct.location-canonicalization", "text", 20000),
  "direct.state-curation": spec("direct.state-curation", "text", 20000),
  "direct.state-binding": spec("direct.state-binding", "tool", 16000),
  "metadata.global": spec("metadata.global", "text", 16000),
  "metadata.descriptions": spec("metadata.descriptions", "text", 16000),
  "synopsis.chunk": spec("synopsis.chunk", "text", 4096),
  "synopsis.episode": spec("synopsis.episode", "text", 4096),
  // Aggregation output is short, but reasoning models can consume several
  // thousand hidden tokens before emitting it. Use the long-text budget so
  // valid arc and whole-script summaries are not cut off mid-response.
  "synopsis.arc": spec("synopsis.arc", "text", 16000),
  "synopsis.script-overview": spec("synopsis.script-overview", "text", 16000),
  // Locate where the actual screenplay body begins: a cheap text classifier
  // scanned over 50-line windows, emitting just `起点: <行号>` / `起点: 无`. Text
  // (not tool) mode so it runs on the cheap deepseek-flash profile, which does
  // not support function calling. The reply is tiny, but reasoning models spend
  // hidden tokens first — too small a budget truncates (finish_reason=length) and
  // the whole call throws. A big cap costs nothing extra (the call stops when the
  // model is done); it only prevents truncation, so match normalize's headroom.
  "ingest.body-start": spec("ingest.body-start", "text", 16000),
  // Compress a folded run of ~10 episodes' aged synopses into one arc paragraph.
  // Fires rarely (only when the synopsis exceeds budget); output is a short
  // paragraph, so a modest budget suffices (headroom for a reasoning model).
  "ingest.arc-summary": spec("ingest.arc-summary", "text", 4096),
  "ingest.text-normalize": spec("ingest.text-normalize", "text", 32000),
  // Serial per-WINDOW asset/state annotation: model reads the running context + a
  // token-bounded window of 1+ episodes (~10k body tokens), returns a structured
  // Delta (SERIAL_DELTA_SCHEMA) covering all their scenes via native tool-calling —
  // canonical names + aliases as separate fields, so no markdown delimiter can
  // mangle a name. Output scales with the window's scene count → large cap so a
  // dense window never truncates (a big cap costs nothing when small). Tool mode:
  // the serial profile (ingest-normalize) must be a function-calling model.
  "ingest.episode-assets": spec("ingest.episode-assets", "tool", 32000),
  // Repair pass: names that appear in the body but resolved to nothing in the
  // final registry (a missed merge). The model says merge-into-existing or new.
  "ingest.name-fix": spec("ingest.name-fix", "tool", 16000),
  "ingest.mention-resolve": spec("ingest.mention-resolve", "text", 8000),
  "ingest.asset-extract": spec("ingest.asset-extract", "text", 8000),
  "ingest.asset-curate": spec("ingest.asset-curate", "text", 32000),
  "ingest.asset-recall": spec("ingest.asset-recall", "text", 65536),
  "ingest.asset-refine": spec("ingest.asset-refine", "text", 32000),
  "ingest.state-reconcile": spec("ingest.state-reconcile", "text", 32000),
  // Final whole-script asset consistency audit. It reads a compact global
  // roster and returns structured findings; no automatic merge is applied.
  "ingest.asset-audit": spec("ingest.asset-audit", "tool", 32000),
  "ingest.speaker-audit": spec("ingest.speaker-audit", "tool", 16000),
  // Video understanding (Gemini, §10.2). One serial pass per episode: watches the
  // video with the running cross-episode context and emits the episode content +
  // a registry delta in one structured JSON response. Large output budget (§10.5
  // verified 65536 for a full episode's scenes).
  "ingest.video-serial": spec("ingest.video-serial", "video", 65536),
} satisfies Record<LlmTaskId, LlmTaskSpec>;

export const LLM_TASK_IDS = Object.keys(LLM_TASK_REGISTRY).filter(isLlmTaskId);

export function isLlmTaskId(value: string): value is LlmTaskId {
  return Object.hasOwn(LLM_TASK_REGISTRY, value);
}
